// frontend_MainDashboard/src/hooks/api/useContactApi.ts

import { useCallback } from 'react';
import { useApiBase, ApiError } from './useApiBase';
import {
  ContactCategory,
  PhonebookContact,
  ImportResult,
} from '@openmirror/shared';

// Enhanced phonebook API configuration
export const PHONEBOOK_API_CONFIG = {
  BULK_IMPORT_CHUNK_SIZE: 100,
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
  EXPORT_TIMEOUT: 30000, // 30 seconds for export operations
  IMPORT_TIMEOUT: 60000,  // 60 seconds for import operations
} as const;

// Enhanced phonebook API types
export interface CategoryCreatePayload {
  name: string;
}

export interface CategoryUpdatePayload {
  name: string;
}

export interface CategoryResponse {
  success: boolean;
  category?: ContactCategory;
  message?: string;
}

export interface CategoriesResponse {
  success: boolean;
  categories?: ContactCategory[];
  message?: string;
}

export interface ContactCreatePayload {
  name: string;
  number: string;
  category: string;
}

export interface ContactUpdatePayload {
  name?: string;
  number?: string;
  category?: string;
}

export interface ContactResponse {
  success: boolean;
  contact?: PhonebookContact;
  message?: string;
}

export interface ContactsResponse {
  success: boolean;
  contacts?: PhonebookContact[];
  message?: string;
}

export interface BulkImportPayload {
  contacts: Array<{
    name: string;
    number: string;
    category: string;
  }>;
  duplicateHandling: 'skip' | 'update' | 'merge';
}

export interface BulkImportResponse {
  success: boolean;
  result?: ImportResult;
  message?: string;
}

export interface ExportContactsPayload {
  format: 'csv' | 'excel';
  categoryFilter?: string;
  searchFilter?: string;
}

export interface ExportContactsResponse {
  success: boolean;
  downloadUrl?: string;
  filename?: string;
  message?: string;
}

export interface ContactApiHook {
  // Category management
  getContactCategories: () => Promise<CategoriesResponse>;
  createContactCategory: (payload: CategoryCreatePayload) => Promise<CategoryResponse>;
  updateContactCategory: (categoryId: string, payload: CategoryUpdatePayload) => Promise<CategoryResponse>;
  deleteContactCategory: (categoryId: string) => Promise<{ success: boolean; message?: string }>;
  
  // Contact management
  getPhonebookContacts: () => Promise<ContactsResponse>;
  createPhonebookContact: (payload: ContactCreatePayload) => Promise<ContactResponse>;
  updatePhonebookContact: (contactId: string, payload: ContactUpdatePayload) => Promise<ContactResponse>;
  deletePhonebookContact: (contactId: string) => Promise<{ success: boolean; message?: string }>;
  
  // Bulk operations
  bulkImportContacts: (payload: BulkImportPayload, onProgress?: (progress: number) => void) => Promise<BulkImportResponse>;
  exportContacts: (payload: ExportContactsPayload) => Promise<ExportContactsResponse>;
}

export const useContactApi = (): ContactApiHook => {
  const { makeRequestWithRetry } = useApiBase();

  // Enhanced error logging for phonebook operations
  const logPhonebookError = useCallback((operation: string, error: ApiError, context?: any) => {
    console.error(`Phonebook API Error [${operation}]:`, {
      message: error.message,
      status: error.status,
      data: error.data,
      context,
      timestamp: new Date().toISOString()
    });
  }, []);

  const getContactCategories = useCallback(async (): Promise<CategoriesResponse> => {
    try {
      return await makeRequestWithRetry<CategoriesResponse>('/client/phonebook/categories', 'GET');
    } catch (error) {
      logPhonebookError('getContactCategories', error as ApiError);
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const createContactCategory = useCallback(async (payload: CategoryCreatePayload): Promise<CategoryResponse> => {
    try {
      return await makeRequestWithRetry<CategoryResponse, CategoryCreatePayload>('/client/phonebook/categories', 'POST', payload);
    } catch (error) {
      logPhonebookError('createContactCategory', error as ApiError, { payload });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const updateContactCategory = useCallback(async (categoryId: string, payload: CategoryUpdatePayload): Promise<CategoryResponse> => {
    try {
      return await makeRequestWithRetry<CategoryResponse, CategoryUpdatePayload>(`/client/phonebook/categories/${categoryId}`, 'PUT', payload);
    } catch (error) {
      logPhonebookError('updateContactCategory', error as ApiError, { categoryId, payload });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const deleteContactCategory = useCallback(async (categoryId: string): Promise<{ success: boolean; message?: string }> => {
    try {
      return await makeRequestWithRetry<{ success: boolean; message?: string }>(`/client/phonebook/categories/${categoryId}`, 'DELETE');
    } catch (error) {
      logPhonebookError('deleteContactCategory', error as ApiError, { categoryId });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const getPhonebookContacts = useCallback(async (): Promise<ContactsResponse> => {
    try {
      return await makeRequestWithRetry<ContactsResponse>('/client/phonebook/contacts', 'GET');
    } catch (error) {
      logPhonebookError('getPhonebookContacts', error as ApiError);
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const createPhonebookContact = useCallback(async (payload: ContactCreatePayload): Promise<ContactResponse> => {
    try {
      return await makeRequestWithRetry<ContactResponse, ContactCreatePayload>('/client/phonebook/contacts', 'POST', payload);
    } catch (error) {
      logPhonebookError('createPhonebookContact', error as ApiError, { payload });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const updatePhonebookContact = useCallback(async (contactId: string, payload: ContactUpdatePayload): Promise<ContactResponse> => {
    try {
      return await makeRequestWithRetry<ContactResponse, ContactUpdatePayload>(`/client/phonebook/contacts/${contactId}`, 'PUT', payload);
    } catch (error) {
      logPhonebookError('updatePhonebookContact', error as ApiError, { contactId, payload });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const deletePhonebookContact = useCallback(async (contactId: string): Promise<{ success: boolean; message?: string }> => {
    try {
      return await makeRequestWithRetry<{ success: boolean; message?: string }>(`/client/phonebook/contacts/${contactId}`, 'DELETE');
    } catch (error) {
      logPhonebookError('deletePhonebookContact', error as ApiError, { contactId });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const bulkImportContacts = useCallback(async (
    payload: BulkImportPayload, 
    onProgress?: (progress: number) => void
  ): Promise<BulkImportResponse> => {
    try {
      // For large imports, we might want to chunk the data
      const chunkSize = PHONEBOOK_API_CONFIG.BULK_IMPORT_CHUNK_SIZE;
      const chunks = [];
      
      for (let i = 0; i < payload.contacts.length; i += chunkSize) {
        chunks.push(payload.contacts.slice(i, i + chunkSize));
      }
      
      if (chunks.length === 1) {
        // Single request for small imports
        return await makeRequestWithRetry<BulkImportResponse, BulkImportPayload>(
          '/client/phonebook/bulk-import', 
          'POST', 
          payload
        );
      }
      
      // Process chunks for large imports
      let totalSuccessful = 0;
      let totalFailed = 0;
      let totalDuplicates = 0;
      const allErrors: string[] = [];
      
      for (let i = 0; i < chunks.length; i++) {
        const chunkPayload: BulkImportPayload = {
          ...payload,
          contacts: chunks[i]
        };
        
        try {
          const chunkResult = await makeRequestWithRetry<BulkImportResponse, BulkImportPayload>(
            '/client/phonebook/bulk-import', 
            'POST', 
            chunkPayload
          );
          
          if (chunkResult.success && chunkResult.result) {
            totalSuccessful += chunkResult.result.successful;
            totalFailed += chunkResult.result.failed;
            totalDuplicates += chunkResult.result.duplicates;
            allErrors.push(...chunkResult.result.errors);
          }
          
          // Report progress
          if (onProgress) {
            const progress = ((i + 1) / chunks.length) * 100;
            onProgress(progress);
          }
        } catch (error) {
          const apiError = error as ApiError;
          logPhonebookError(`bulkImportContacts - Chunk ${i + 1}`, apiError, { chunkSize: chunks[i].length });
          totalFailed += chunks[i].length;
          allErrors.push(`Chunk ${i + 1} failed: ${apiError.message}`);
        }
      }
      
      return {
        success: true,
        result: {
          successful: totalSuccessful,
          failed: totalFailed,
          duplicates: totalDuplicates,
          errors: allErrors
        }
      };
    } catch (error) {
      logPhonebookError('bulkImportContacts', error as ApiError, { 
        totalContacts: payload.contacts.length,
        duplicateHandling: payload.duplicateHandling 
      });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  const exportContacts = useCallback(async (payload: ExportContactsPayload): Promise<ExportContactsResponse> => {
    try {
      return await makeRequestWithRetry<ExportContactsResponse, ExportContactsPayload>('/client/phonebook/export', 'POST', payload);
    } catch (error) {
      logPhonebookError('exportContacts', error as ApiError, { payload });
      throw error;
    }
  }, [makeRequestWithRetry, logPhonebookError]);

  return {
    getContactCategories,
    createContactCategory,
    updateContactCategory,
    deleteContactCategory,
    getPhonebookContacts,
    createPhonebookContact,
    updatePhonebookContact,
    deletePhonebookContact,
    bulkImportContacts,
    exportContacts,
  };
};
